import React, { createContext, useState, useCallback, useEffect } from 'react';
import { useApi, useRequiredContext } from '../hooks';
import { theme } from '../utils/theme';
import { tSettingsResponse, setCustomFont } from './zod';

const defaultSettingsObject: tSettingsResponse = {
  interactionColor: theme.colors.primary['80'],
  cornerRadius: '24px',
  fontFamily: undefined,
  fonts: [
    {
      family: 'Inter, Helvetica',
      url: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap',
    },
  ],
  redirectUrl: undefined,
};

export const SettingsContext = createContext<tSettingsResponse>(
  defaultSettingsObject,
);

export const SettingsProvider = ({ children }) => {
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [settings, setSettings] = useState<tSettingsResponse>(
    defaultSettingsObject,
  );
  const api = useApi();

  const getAppSettings = useCallback(async () => {
    const response = await api('settings', 'GET', '*');

    if (response.data) {
      const fontFams = setCustomFont(response.data.fonts);

      document.body.style.setProperty('--PhoneInputInputBorderRadius', `${response.data.cornerRadius}px`);

      setSettings({
        ...response.data,
        cornerRadius: `${response.data.cornerRadius}px`,
        fontFamily: fontFams,
      });
      setSettingsLoaded(true);
    }
  }, [api]);

  useEffect(() => {
    getAppSettings();
  }, [getAppSettings]);

  SettingsContext.displayName = 'SettingsProvider';
  return (
    <SettingsContext.Provider value={settings}>
      {settings && settingsLoaded && children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useRequiredContext(SettingsContext);
